import { useState, useMemo, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Table, TableHeader, TableBody, TableRow, TableCell } from "../components/ui/table";
import { Checkbox } from "../components/ui/checkbox";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Label } from "../components/ui/label";
import { Plus, ShoppingCart, Pencil, Check, X, Trash2, Tag, FileOutput, Search } from "lucide-react";
import { ZoomableImage } from "./ZoomableImage";
import { ResizableTh } from "./ResizableTh";
import { SampleQtyBadge } from "../pages/SamplesPage";
import { useWarehouses, formatWarehouseBreakdown } from "../lib/useWarehouses";
import { CiQtyCell } from "./CiQtyCell";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { ContractDialog } from "./ContractDialog";
import { ManualEditMarker } from "./ManualEditMarker";

function formatSalesPrice(row, currency) {
  const value = currency === "CAD" ? row.suggested_price_cad : row.suggested_price_usd;
  if (value == null) return "-";
  return currency === "CAD" ? `C$${Number(value).toFixed(2)}` : `$${Number(value).toFixed(2)}`;
}

const CONTAINER_CBM_MM3 = 65_000_000;

function computeContainerQty(length, width, height, pcs) {
  const l = parseFloat(length), w = parseFloat(width), h = parseFloat(height), p = parseInt(pcs, 10);
  if (!l || !w || !h || !p) return null;
  const qty = Math.floor((CONTAINER_CBM_MM3 * p) / (l * w * h));
  return qty > 0 ? qty : null;
}

function computeGroups(rows) {
  const groupSize = new Array(rows.length).fill(0);
  let i = 0;
  while (i < rows.length) {
    let j = i;
    while (j < rows.length && rows[j].product_id === rows[i].product_id) j++;
    groupSize[i] = j - i;
    i = j;
  }
  return groupSize;
}

// Master column catalogue. `level: "product"` uses rowspan (rendered once per
// product, shared across all its variant rows); `level: "variant"` renders on
// every row. Keep IDs stable — they're stored inside saved Views.
export const ALL_PRODUCT_COLUMNS = [
  { id: "checkbox",           label: "",                    level: "product", width: 44,  minWidth: 40,  align: "left" },
  { id: "photo",              label: "Photo",               level: "product", width: 140, minWidth: 90,  align: "left" },
  { id: "item_no",            label: "Item No",             level: "product", width: 112, minWidth: 80,  align: "left" },
  { id: "tag",                label: "Tag",                 level: "product", width: 92,  minWidth: 60,  align: "left" },
  { id: "description",        label: "Description",         level: "product", width: 260, minWidth: 160, align: "left" },
  { id: "bulky",              label: "Bulky",               level: "product", width: 96,  minWidth: 70,  align: "left" },
  { id: "sample_qty",         label: "Sample Stock",        level: "product", width: 96,  minWidth: 70,  align: "right" },
  { id: "size",               label: "Size",                level: "variant", width: 112, minWidth: 70,  align: "left" },
  { id: "pcs_ctn",            label: "PCS/CTN",             level: "variant", width: 92,  minWidth: 70,  align: "right" },
  { id: "ci_qty",             label: "CI Inv Qty",          level: "variant", width: 96,  minWidth: 70,  align: "right" },
  { id: "factory_cost",       label: "Factory Cost (RMB)",  level: "variant", width: 140, minWidth: 100, align: "right" },
  { id: "sales_price",        label: "Sales Price",         level: "variant", width: 140, minWidth: 100, align: "right" },
  { id: "carton_dims",        label: "Carton L×W×H (cm)",   level: "variant", width: 160, minWidth: 120, align: "right" },
  { id: "qty_40ft",           label: "40' Qty (pcs)",       level: "variant", width: 112, minWidth: 80,  align: "right" },
  { id: "actions",            label: "Actions",             level: "variant", width: 84,  minWidth: 70,  align: "right" },
  // New columns synced from FileMaker but not previously shown:
  { id: "chinese_name",       label: "Chinese Name",        level: "variant", width: 180, minWidth: 100, align: "left" },
  { id: "gt_sku",             label: "GT SKU",              level: "variant", width: 120, minWidth: 80,  align: "left" },
  { id: "factory_name",       label: "Factory",             level: "variant", width: 140, minWidth: 90,  align: "left" },
  { id: "purchaser",          label: "Purchaser",           level: "variant", width: 100, minWidth: 70,  align: "left" },
  { id: "approval",           label: "Approval",            level: "variant", width: 90,  minWidth: 70,  align: "left" },
  { id: "contract_no",        label: "Contract No",         level: "variant", width: 110, minWidth: 80,  align: "left" },
  { id: "invoice_no",         label: "Invoice No",          level: "variant", width: 120, minWidth: 80,  align: "left" },
  { id: "po_number",          label: "PO No",               level: "variant", width: 130, minWidth: 90,  align: "left" },
  { id: "contract_date",      label: "Contract Date",       level: "variant", width: 110, minWidth: 90,  align: "left" },
  { id: "shipping_date",      label: "Shipping Date",       level: "variant", width: 110, minWidth: 90,  align: "left" },
  { id: "po_shipping_date",   label: "PO Shipping Date",    level: "variant", width: 130, minWidth: 100, align: "left" },
  { id: "net_weight",         label: "Net Wt (kg)",         level: "variant", width: 90,  minWidth: 70,  align: "right" },
  { id: "gross_weight",       label: "Gross Wt (kg)",       level: "variant", width: 90,  minWidth: 70,  align: "right" },
  { id: "barcode",            label: "Barcode",             level: "variant", width: 130, minWidth: 90,  align: "left" },
  { id: "customer_product_no",label: "Customer Prod No",    level: "variant", width: 130, minWidth: 90,  align: "left" },
  { id: "company_product_no", label: "Company Prod No",     level: "variant", width: 130, minWidth: 90,  align: "left" },
  { id: "english_description",label: "English Description", level: "variant", width: 220, minWidth: 140, align: "left" },
  { id: "budget_no",          label: "Budget No",           level: "product", width: 110, minWidth: 80,  align: "left" },
  // iter 83: Category / Available Date columns (all product-level)
  // iter 209 (2026-02): customer_name column removed per user request.
  { id: "category",           label: "Category",            level: "product", width: 110, minWidth: 80,  align: "left" },
  { id: "available_date",     label: "Available Date",      level: "product", width: 120, minWidth: 90,  align: "left" },
  // iter 78: Quality / Packaging requirements from product_main
  { id: "quality_requirement",  label: "Quality Req",     level: "product", width: 220, minWidth: 140, align: "left" },
  { id: "packaging_requirement",label: "Packaging Req",   level: "product", width: 220, minWidth: 140, align: "left" },
  // iter 77: sales price / GT price / FX rate from FileMaker
  { id: "price_order",        label: "Sales Price (Order)",level: "variant", width: 130, minWidth: 90,  align: "right" },
  { id: "gt_price",           label: "GT Price",            level: "variant", width: 110, minWidth: 80,  align: "right" },
  { id: "exchange_rate",      label: "FX Rate (Cost/GT)",   level: "variant", width: 120, minWidth: 90,  align: "right" },
  // iter 110: Amount = sales price × order qty; totals up when the column is
  // visible so users can see the summed order value at the bottom of the
  // table.
  { id: "amount",             label: "Amount (Sales × Qty)",level: "variant", width: 130, minWidth: 100, align: "right" },
  // iter 178x: 3 additional FM data_api fields exposed as optional columns.
  // company_product_no already existed (same source as item_no); the two
  // brand-new ones live at variant level. FM sync overwrites on every pull,
  // no user-edit protection (per user directive).
  { id: "shipping_qty",       label: "Shipping Qty",        level: "variant", width: 110, minWidth: 80,  align: "right" },
  { id: "cbm_total",          label: "CBM Total",           level: "variant", width: 110, minWidth: 80,  align: "right" },
  // iter 179 / 179b: Order Quantity (variant-level, synced from FM data_api 数量)
  // + Purchase Reference Price (variant-level, from data_api.参考采购价 after
  // user added the field to the data_api layout — was product-level in iter 179).
  { id: "qty",                     label: "Order Qty",              level: "variant", width: 100, minWidth: 70,  align: "right" },
  { id: "purchase_reference_price",label: "Ref Purchase Price (¥)", level: "variant", width: 140, minWidth: 100, align: "right" },
];

const COLUMN_BY_ID = Object.fromEntries(ALL_PRODUCT_COLUMNS.map((c) => [c.id, c]));
// iter 210 (2026-02): re-export so exportProductsExcel.js reuses the exact
// same column metadata + value extractors as the on-screen table renderer.
export { COLUMN_BY_ID };

// iter 247: column id -> backend sort_by key (SORTABLE_FIELDS whitelist).
export const COLUMN_SORT_KEYS = {
  item_no: "item_no", tag: "tag", description: "description", bulky: "is_bulky", sample_qty: "sample_qty", size: "size",
  pcs_ctn: "pcs_per_ctn", ci_qty: "ci_inventory_qty", factory_cost: "purchase_price",
  chinese_name: "chinese_name", gt_sku: "gt_sku", factory_name: "factory_name", purchaser: "purchaser",
  approval: "approval", contract_no: "contract_no", invoice_no: "invoice_no", contract_date: "contract_date",
  shipping_date: "shipping_date", po_shipping_date: "po_shipping_date", net_weight: "net_weight",
  gross_weight: "gross_weight", barcode: "upc", customer_product_no: "customer_product_no",
  company_product_no: "company_product_no", english_description: "english_description", budget_no: "budget_no",
  category: "category", available_date: "available_date", quality_requirement: "quality_requirement",
  packaging_requirement: "packaging_requirement", price_order: "price_order", gt_price: "gt_price",
  amount: "amount", shipping_qty: "shipping_qty", cbm_total: "cbm_total", qty: "qty",
  purchase_reference_price: "purchase_reference_price",
};

// iter 199 (2026-02): 紧凑模式下按内容自动收缩列宽。用 canvas.measureText
// 一次性测量,不触发 DOM 重排。用户手动 resize 后 (columnWidths[id] 有值)
// 该列保留手工宽度不被自动值覆盖。
// iter 210 (2026-02): re-exported so exportProductsExcel.js reuses the exact
// same value extraction logic — 保证导出 Excel 内容跟屏幕上看到的一模一样。
export const AUTO_FIT_EXTRACTORS = {
  item_no: (r) => r.item_no,
  tag: (r) => r.tag,
  description: (r) => r.description,
  bulky: (r) => (r.is_bulky ? "Yes" : ""),
  size: (r) => r.size,
  pcs_ctn: (r) => r.pcs_per_ctn,
  ci_qty: (r) => r.ci_inventory_qty,
  factory_cost: (r) => r.purchase_price,
  sales_price: (r) => r.suggested_price_usd, // 显示会是 "$xx.xx",取原值近似即可
  carton_dims: (r) => (r.carton_length && r.carton_width && r.carton_height ? `${r.carton_length}×${r.carton_width}×${r.carton_height}` : ""),
  qty_40ft: (r) => r.qty_40ft,
  sample_qty: (r) => r.sample_qty,
  chinese_name: (r) => r.chinese_name,
  gt_sku: (r) => r.gt_sku,
  factory_name: (r) => r.factory_name,
  purchaser: (r) => r.purchaser,
  approval: (r) => r.approval,
  contract_no: (r) => r.contract_no,
  invoice_no: (r) => r.invoice_no,
  po_number: (r) => r.po_number,
  contract_date: (r) => r.contract_date || r.contract_working_date,
  shipping_date: (r) => r.shipping_date,
  po_shipping_date: (r) => r.po_shipping_date,
  net_weight: (r) => r.net_weight,
  gross_weight: (r) => r.gross_weight,
  barcode: (r) => r.barcode,
  customer_product_no: (r) => r.customer_product_no,
  company_product_no: (r) => r.company_product_no,
  english_description: (r) => r.english_description,
  budget_no: (r) => r.budget_no,
  customer_name: (r) => r.customer_name,
  category: (r) => r.category,
  available_date: (r) => r.available_date,
  quality_requirement: (r) => r.quality_requirement,
  packaging_requirement: (r) => r.packaging_requirement,
  price_order: (r) => r.price_order,
  gt_price: (r) => r.gt_price,
  exchange_rate: (r) => r.exchange_rate,
  amount: (r) => (r.amount != null ? Number(r.amount).toFixed(2) : ""),
  shipping_qty: (r) => r.shipping_qty,
  cbm_total: (r) => r.cbm_total,
  qty: (r) => r.qty,
  purchase_reference_price: (r) => r.purchase_reference_price,
};

// 固定不参与 auto-fit 的列 (用组件默认宽度)。photo/checkbox/actions/bulky
// 展示的是图片、勾选框或按钮组,靠文字长度没意义。
const AUTO_FIT_SKIP = new Set(["checkbox", "photo", "actions"]);

// 紧凑模式下固定列的宽度覆盖 (照片列显著变窄以省版面)。
const COMPACT_FIXED_WIDTHS = {
  checkbox: 32,
  photo: 100,     // 图片 80px + 8px margin + 12px 边距
  actions: 68,
};

// 单例 canvas,避免每次 useMemo 都新建
let _measureCanvas = null;
function _getMeasureCtx() {
  if (typeof document === "undefined") return null;
  if (!_measureCanvas) {
    _measureCanvas = document.createElement("canvas").getContext("2d");
    _measureCanvas.font = '12px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';
  }
  return _measureCanvas;
}

function computeAutoFitWidths(rows, columnIds) {
  const ctx = _getMeasureCtx();
  if (!ctx) return {};
  const CELL_PADDING_PX = 14; // 紧凑模式左右各 6px + 2px 安全边距
  const HARD_CAP = 260;       // 描述/需求类字段超过这个宽度就让文本换行
  const HEADER_WEIGHT = 1.15; // 表头 12px 加粗,略宽于正文
  const out = {};
  for (const id of columnIds) {
    if (AUTO_FIT_SKIP.has(id)) continue;
    const col = COLUMN_BY_ID[id];
    if (!col) continue;
    const extractor = AUTO_FIT_EXTRACTORS[id];
    // 表头文字宽度 (label 可能是中文,需要按加粗字体估算)
    const headerWidth = ctx.measureText(col.label || "").width * HEADER_WEIGHT;
    let contentMax = 0;
    if (extractor) {
      // 只扫当前页数据,足够代表,不消耗太多算力
      for (const r of rows) {
        const v = extractor(r);
        if (v == null || v === "") continue;
        const s = String(v);
        // 长文本经常包含换行/多单词,取最长一段作为宽度基准
        const longest = s.split(/[\s\n]+/).reduce((mx, seg) => Math.max(mx, ctx.measureText(seg).width), 0);
        if (longest > contentMax) contentMax = longest;
      }
    }
    const natural = Math.max(headerWidth, contentMax) + CELL_PADDING_PX;
    out[id] = Math.max(col.minWidth || 40, Math.min(HARD_CAP, Math.ceil(natural)));
  }
  return out;
}

export const DEFAULT_VISIBLE_COLUMNS = [
  "checkbox", "photo", "item_no", "tag", "description", "bulky", "size",
  "pcs_ctn", "ci_qty", "factory_cost", "sales_price", "carton_dims", "qty_40ft", "actions",
];

function VariantCell({ colId, row, priceCurrency, isEditing, editForm, hqContainerQty, updateEditField, isAdmin, onCartClick, onRowClick, onToggleApproval, onOpenContract }) {
  const align = COLUMN_BY_ID[colId]?.align === "right" ? "text-right" : "text-left";
  switch (colId) {
    case "size":
      return isEditing ? (
        <TableCell className="py-2" onClick={(e) => e.stopPropagation()}>
          <Input data-testid={`edit-size-${row.entry_id}`} value={editForm.size} onChange={(e) => updateEditField("size", e.target.value)} className="h-8 rounded-md text-sm" />
        </TableCell>
      ) : (
        <TableCell className="text-[14px] text-gray-500 truncate">{row.size || "-"}</TableCell>
      );
    case "pcs_ctn":
      return isEditing ? (
        <TableCell className="py-2" onClick={(e) => e.stopPropagation()}>
          <Input data-testid={`edit-pcs-per-ctn-${row.entry_id}`} type="number" value={editForm.pcs_per_ctn} onChange={(e) => updateEditField("pcs_per_ctn", e.target.value)} className="h-8 rounded-md text-sm text-right font-mono" />
        </TableCell>
      ) : (
        <TableCell className="font-mono text-[14px] text-right text-gray-500">{row.pcs_per_ctn ?? "-"}</TableCell>
      );
    case "ci_qty":
      return <TableCell className="font-mono text-[14px] text-right text-gray-500" data-testid={`product-ci-inventory-qty-${row.entry_id}`}><CiQtyCell row={row} /></TableCell>;
    case "price_order":
      return (
        <TableCell className="font-mono text-sm text-right text-gray-700" data-testid={`product-price-order-${row.entry_id}`}>
          <div>{row.price_order != null ? Number(row.price_order).toFixed(2) : "-"}</div>
          {row.gt_price != null && <div className="mt-0.5">C${Number(row.gt_price).toFixed(2)}</div>}
          {row.qty != null && (
            <div className="text-[10px] text-gray-400 font-mono mt-0.5" title="Order Quantity">×{row.qty}</div>
          )}
        </TableCell>
      );
    case "gt_price":
      return <TableCell className="font-mono text-sm text-right text-gray-700" data-testid={`product-gt-price-${row.entry_id}`}>{row.gt_price != null ? `C$${Number(row.gt_price).toFixed(2)}` : "-"}</TableCell>;
    case "exchange_rate": {
      const isYes = (row.approval || "").toLowerCase() === "yes";
      const badgeCls = isYes
        ? "bg-emerald-50 text-emerald-700 border-emerald-200"
        : row.approval
          ? "bg-gray-100 text-gray-500 border-gray-200"
          : "bg-white text-gray-300 border-gray-200 border-dashed";
      return (
        <TableCell className="font-mono text-sm text-right text-gray-700" data-testid={`product-exchange-rate-${row.entry_id}`} title="采购价 / (GT Price 或 Price_Order)">
          <div>{row.exchange_rate != null ? Number(row.exchange_rate).toFixed(2) : "-"}</div>
          <div className="mt-0.5" onClick={(e) => e.stopPropagation()}>
            {isAdmin ? (
              <button
                data-testid={`approval-inline-${row.entry_id}`}
                onClick={() => onToggleApproval && onToggleApproval(row)}
                title={`Approval: ${row.approval || "(empty)"} — click to toggle`}
                className={`inline-flex px-2 py-0.5 rounded-md text-sm font-medium border cursor-pointer hover:brightness-95 transition-all ${badgeCls}`}
              >
                {row.approval || "-"}
              </button>
            ) : (
              <span className={`inline-flex px-2 py-0.5 rounded-md text-sm font-medium border ${badgeCls}`}>{row.approval || "-"}</span>
            )}
          </div>
        </TableCell>
      );
    }
    case "amount": {
      // iter 257: backend computes amount = 外销价 × 数量, falling back to
      // 采购价 ÷ USD→RMB 汇率 × 数量 when 外销价 is 0 / empty.
      const amt = row.amount != null ? Number(row.amount) : null;
      const usedCost = amt != null && !(Number(row.price_order) > 0);
      return (
        <TableCell
          className={`font-mono text-sm text-right ${usedCost ? "text-gray-500 italic" : "text-gray-900"}`}
          data-testid={`product-amount-${row.entry_id}`}
          title={amt == null ? "Sales price / cost or Qty missing" : usedCost ? `Factory cost ÷ USD→RMB rate × Qty ${row.qty}` : `Sales ${Number(row.price_order).toFixed(2)} × Qty ${row.qty}`}
        >
          {amt != null ? amt.toFixed(2) : "-"}
        </TableCell>
      );
    }
    // iter 178x: FM data_api 3 additional fields as optional columns.
    case "shipping_qty":
      return <TableCell className="font-mono text-[14px] text-right text-gray-700" data-testid={`product-shipping-qty-${row.entry_id}`}>{row.shipping_qty != null ? Number(row.shipping_qty).toLocaleString() : "-"}</TableCell>;
    case "cbm_total":
      return <TableCell className="font-mono text-[14px] text-right text-gray-700" data-testid={`product-cbm-total-${row.entry_id}`}>{row.cbm_total != null ? Number(row.cbm_total).toFixed(3) : "-"}</TableCell>;
    // iter 179: Order Quantity from FM (数量) — variant-level.
    case "qty":
      return <TableCell className="font-mono text-[14px] text-right text-gray-700" data-testid={`product-qty-${row.entry_id}`}>{row.qty != null ? Number(row.qty).toLocaleString() : "-"}</TableCell>;
    // iter 179b: Ref Purchase Price — variant-level after user added
    // 参考采购价 to the data_api layout. Was product-level in iter 179.
    case "purchase_reference_price":
      return <TableCell className="font-mono text-[14px] text-right text-gray-700" data-testid={`product-purchase-ref-price-${row.entry_id}`}>{row.purchase_reference_price != null ? `¥${Number(row.purchase_reference_price).toFixed(2)}` : "-"}</TableCell>;
    case "factory_cost":
      return isEditing ? (
        <TableCell className="py-2" onClick={(e) => e.stopPropagation()}>
          <Input data-testid={`edit-purchase-price-${row.entry_id}`} type="number" step="any" value={editForm.purchase_price} onChange={(e) => updateEditField("purchase_price", e.target.value)} className="h-8 rounded-md text-sm text-right font-mono" />
        </TableCell>
      ) : (
        <TableCell className="font-mono text-[14px] text-right font-bold text-gray-800" data-testid={`product-factory-cost-${row.entry_id}`}>{row.purchase_price != null ? `¥${Number(row.purchase_price).toFixed(2)}` : "-"}</TableCell>
      );
    case "sales_price":
      return (
        <TableCell className="font-mono text-xs text-right font-bold text-black" data-testid={`product-sales-price-${row.entry_id}`}>
          {formatSalesPrice(row, priceCurrency)}
        </TableCell>
      );
    case "carton_dims":
      return isEditing ? (
        <TableCell className="py-2" onClick={(e) => e.stopPropagation()}>
          <div className="flex items-center gap-1 justify-end">
            <Input data-testid={`edit-carton-length-${row.entry_id}`} type="number" step="any" placeholder="L" value={editForm.carton_length} onChange={(e) => updateEditField("carton_length", e.target.value)} className="h-8 w-16 rounded-md text-xs text-right font-mono px-1.5" />
            <span className="text-gray-300 text-xs">×</span>
            <Input data-testid={`edit-carton-width-${row.entry_id}`} type="number" step="any" placeholder="W" value={editForm.carton_width} onChange={(e) => updateEditField("carton_width", e.target.value)} className="h-8 w-16 rounded-md text-xs text-right font-mono px-1.5" />
            <span className="text-gray-300 text-xs">×</span>
            <Input data-testid={`edit-carton-height-${row.entry_id}`} type="number" step="any" placeholder="H" value={editForm.carton_height} onChange={(e) => updateEditField("carton_height", e.target.value)} className="h-8 w-16 rounded-md text-xs text-right font-mono px-1.5" />
          </div>
        </TableCell>
      ) : (
        <TableCell className="font-mono text-xs text-right text-gray-500" data-testid={`product-carton-dims-${row.entry_id}`}>
          {row.carton_length && row.carton_width && row.carton_height ? (
            <>
              {row.carton_length}×{row.carton_width}×{row.carton_height}
              {row.pcs_per_ctn != null && <span className="ml-1.5 text-gray-400">· {row.pcs_per_ctn}pcs</span>}
            </>
          ) : "-"}
        </TableCell>
      );
    case "qty_40ft":
      return <TableCell className="font-mono text-[14px] text-right text-gray-900" data-testid={`product-hq-container-qty-${row.entry_id}`}>{hqContainerQty != null ? hqContainerQty.toLocaleString() : "-"}</TableCell>;
    // new variant columns — plain read-only cells
    case "chinese_name":
      return <TableCell className="text-xs text-gray-600 truncate" title={row.chinese_name || ""}>{row.chinese_name || "-"}</TableCell>;
    case "gt_sku":
      return <TableCell className="font-mono text-xs text-gray-600 truncate" title={row.gt_sku || ""}>{row.gt_sku || "-"}</TableCell>;
    case "factory_name":
      return (
        <TableCell className="text-xs text-gray-600 truncate" title={`${row.factory_name || ""}${row.purchaser ? ` · 采购 ${row.purchaser}` : ""}`}>
          <div className="truncate">{row.factory_name || "-"}</div>
          {row.purchaser && (
            <div className="text-[10px] text-gray-400 font-mono truncate">Buyer · {row.purchaser}</div>
          )}
        </TableCell>
      );
    case "purchaser":
      return <TableCell className="text-xs text-gray-600">{row.purchaser || "-"}</TableCell>;
    case "approval":
      {
        const label = row.approval || "-";
        const isYes = (row.approval || "").toLowerCase() === "yes";
        const badgeCls = isYes
          ? "bg-emerald-50 text-emerald-700 border-emerald-200"
          : row.approval
            ? "bg-gray-100 text-gray-500 border-gray-200"
            : "bg-white text-gray-300 border-gray-200 border-dashed";
        return (
          <TableCell onClick={(e) => e.stopPropagation()}>
            {isAdmin ? (
              <button
                data-testid={`approval-cell-${row.entry_id}`}
                onClick={() => onToggleApproval && onToggleApproval(row)}
                title={`Approval: ${row.approval || "(empty)"} — click to toggle`}
                className={`inline-flex px-2 py-0.5 rounded-md text-[10px] font-medium border cursor-pointer hover:brightness-95 transition-all ${badgeCls}`}
              >
                {label}
              </button>
            ) : (
              <span className={`inline-flex px-2 py-0.5 rounded-md text-[10px] font-medium border ${badgeCls}`}>{label}</span>
            )}
          </TableCell>
        );
      }
    case "contract_no":
      return (
        <TableCell className="font-mono text-xs text-gray-600 truncate" title={`${row.contract_no || ""}${row.contract_date ? ` (${row.contract_date})` : ""}${row.po_shipping_date ? ` · PO Ship ${row.po_shipping_date}` : ""}`}>
          {row.contract_no ? (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); onOpenContract?.(row.contract_no); }}
              data-testid={`product-contract-no-${row.entry_id}`}
              className="font-semibold text-brand-600 hover:text-brand-700 hover:underline truncate text-left w-full"
            >
              {row.contract_no}
            </button>
          ) : (
            <div className="font-semibold text-gray-800 truncate">-</div>
          )}
          {row.contract_date && <div className="text-[10px] text-gray-400 truncate">{row.contract_date}</div>}
          {row.po_shipping_date && <div className="text-[10px] text-emerald-600 truncate" title="PO Shipping Date">PO {row.po_shipping_date}</div>}
        </TableCell>
      );
    case "contract_date":
      return <TableCell className="font-mono text-xs text-gray-600">{row.contract_date || "-"}</TableCell>;
    case "invoice_no":
      return <TableCell className="font-mono text-xs text-gray-600 truncate" title={row.invoice_no || ""}>{row.invoice_no || "-"}</TableCell>;
    case "po_number":
      return <TableCell className="font-mono text-xs text-gray-600 truncate" title={row.po_number || ""}>{row.po_number || "-"}</TableCell>;
    case "shipping_date":
      return <TableCell className="font-mono text-xs text-gray-600">{row.shipping_date || "-"}</TableCell>;
    case "po_shipping_date":
      return <TableCell className="font-mono text-xs text-emerald-600" title="PO Shipping Date">{row.po_shipping_date || "-"}</TableCell>;
    case "net_weight":
      return <TableCell className="font-mono text-xs text-right text-gray-600">{row.net_weight ?? "-"}</TableCell>;
    case "gross_weight":
      return <TableCell className="font-mono text-xs text-right text-gray-600">{row.gross_weight ?? "-"}</TableCell>;
    case "barcode":
      return <TableCell className="font-mono text-xs text-gray-600 truncate" title={row.upc || row.barcode || ""}>{row.upc || row.barcode || "-"}</TableCell>;
    case "customer_product_no":
      return <TableCell className="font-mono text-xs text-gray-600 truncate" title={row.customer_product_no || ""}>{row.customer_product_no || "-"}</TableCell>;
    case "company_product_no":
      return <TableCell className="font-mono text-xs text-gray-600 truncate" title={row.company_product_no || ""}>{row.company_product_no || "-"}</TableCell>;
    case "english_description":
      return <TableCell className="text-xs text-gray-600 truncate" title={row.english_description || ""}>{row.english_description || "-"}</TableCell>;
    default:
      return <TableCell className={align}>-</TableCell>;
  }
}

// iter 275: total across warehouses; hover shows the per-warehouse split.
function SampleQtyCell({ row }) {
  const { nameOf } = useWarehouses();
  const title = formatWarehouseBreakdown(row.sample_by_wh, nameOf);
  return <span title={title} data-testid={`product-sample-qty-tip-${row.product_id}`}><SampleQtyBadge qty={row.sample_qty} /></span>;
}


function ProductCell({ colId, row, isGroupStart, groupRowSpan, selected, toggleSelected, onAddVariant, onToggleBulky, onLookupSimilar, onRevertManual, density = "compact" }) {
  if (!isGroupStart) return null;
  const cellProps = { rowSpan: groupRowSpan, className: "py-3 align-top border-r border-gray-100" };
  switch (colId) {
    case "checkbox":
      return (
        <TableCell {...cellProps} onClick={(e) => e.stopPropagation()}>
          <div className="flex flex-col items-center gap-1.5">
            <Checkbox data-testid={`product-select-${row.product_id}`} checked={selected.has(row.product_id)} onCheckedChange={() => toggleSelected(row.product_id)} />
            <button
              data-testid={`similar-items-${row.product_id}`}
              onClick={(e) => { e.stopPropagation(); onLookupSimilar && onLookupSimilar(row); }}
              title="Find similar Item Numbers"
              className="text-gray-300 hover:text-brand-600 transition-colors"
            >
              <Search size={13} strokeWidth={1.75} />
            </button>
          </div>
        </TableCell>
      );
    case "photo":
      return (
        <TableCell {...cellProps}>
          <div className="flex flex-col items-start gap-1.5">
            <div className="font-mono text-xs font-bold text-gray-800 leading-tight" data-testid={`product-photo-itemno-${row.product_id}`}>{row.item_no || "-"}</div>
            <ZoomableImage
              src={row.photo_url}
              alt={row.description}
              className={density === "compact" ? "w-20 h-16 object-cover" : "w-28 h-28 object-cover"}
              testId={`product-photo-${row.product_id}`}
            />
          </div>
        </TableCell>
      );
    case "item_no":
      return (
        <TableCell {...cellProps} className={`${cellProps.className} font-mono text-xs font-bold text-gray-800`} data-testid={`product-itemno-${row.product_id}`}>
          {row.item_no || "-"}
        </TableCell>
      );
    case "tag":
      return <TableCell {...cellProps} className={`${cellProps.className} text-sm text-gray-500`} data-testid={`product-tag-${row.product_id}`}>{row.tag || "-"}</TableCell>;
    case "description":
      return (
        <TableCell {...cellProps} className={`${cellProps.className} text-sm text-gray-700`} data-testid={`product-description-${row.product_id}`}>
          <div className="flex items-start justify-between gap-2">
            <span className="flex items-start gap-1.5"><span>{row.description || "-"}</span><ManualEditMarker row={row} onRevert={onRevertManual} /></span>
            <button data-testid={`add-variant-${row.product_id}`} onClick={(e) => { e.stopPropagation(); onAddVariant(row); }} title="Add Variant" className="flex-shrink-0 text-gray-300 hover:text-brand-600 transition-colors">
              <Plus size={14} strokeWidth={1.75} />
            </button>
          </div>
        </TableCell>
      );
    case "sample_qty":
      return <TableCell {...cellProps} className={`${cellProps.className} text-right`} data-testid={`product-sample-qty-${row.product_id}`}><SampleQtyCell row={row} /></TableCell>;
    case "bulky":
      return (
        <TableCell {...cellProps} onClick={(e) => e.stopPropagation()}>
          <button
            data-testid={`toggle-bulky-${row.product_id}`}
            onClick={() => onToggleBulky(row.product_id, !row.is_bulky)}
            className={`text-xs font-medium px-2 py-1 rounded-md transition-colors ${row.is_bulky ? "bg-amber-100 text-amber-700 hover:bg-amber-200" : "bg-gray-100 text-gray-500 hover:bg-gray-200"}`}
          >
            {row.is_bulky ? "Yes" : "No"}
          </button>
        </TableCell>
      );
    case "budget_no":
      return (
        <TableCell {...cellProps} className={`${cellProps.className} font-mono text-xs text-gray-600 whitespace-pre-line break-words`} data-testid={`product-budget-no-${row.product_id}`} title={row.budget_no || ""}>
          {row.budget_no || "-"}
        </TableCell>
      );
    case "quality_requirement":
      return (
        <TableCell {...cellProps} className={`${cellProps.className} text-xs text-gray-600 truncate`} data-testid={`product-quality-req-${row.product_id}`} title={row.quality_requirement || ""}>
          {row.quality_requirement || "-"}
        </TableCell>
      );
    case "packaging_requirement":
      return (
        <TableCell {...cellProps} className={`${cellProps.className} text-xs text-gray-600 truncate`} data-testid={`product-packaging-req-${row.product_id}`} title={row.packaging_requirement || ""}>
          {row.packaging_requirement || "-"}
        </TableCell>
      );
    case "customer_name":
      return (
        <TableCell {...cellProps} className={`${cellProps.className} text-xs text-gray-600 truncate`} data-testid={`product-customer-name-${row.product_id}`} title={row.customer_name || ""}>
          {row.customer_name || "-"}
        </TableCell>
      );
    case "category":
      return (
        <TableCell {...cellProps} className={`${cellProps.className} text-xs text-gray-600 truncate`} data-testid={`product-category-${row.product_id}`}>
          {row.category || "-"}
          {row.subcategory && <span className="text-gray-400"> / {row.subcategory}</span>}
        </TableCell>
      );
    case "available_date":
      return (
        <TableCell {...cellProps} className={`${cellProps.className} font-mono text-xs text-gray-600`} data-testid={`product-available-date-${row.product_id}`}>
          {row.available_date || "-"}
        </TableCell>
      );
    default:
      return <TableCell {...cellProps}>-</TableCell>;
  }
}

export default function InventoryTable({
  rows, totals, onAddVariant, onCartClick, onRowClick, onUpdateVariant, onDeleteVariant, onToggleBulky, onBulkSetTag, onLookupSimilar, onToggleApproval, onRevertManual,
  priceCurrency = "USD", loading,
  visibleColumns, columnWidths, onColumnResize,
  // iter 123: selection state is now owned by Dashboard so it survives
  // view switches. Fall back to local state so this component still works
  // standalone in tests/storybook.
  selected: selectedProp, onToggleSelected, onClearSelected,
  // iter 198 (2026-02): density = "compact" (default, Excel-like grid, 12px)
  // or "comfortable" (legacy roomy layout, 14px). Persisted by parent.
  density = "compact",
  // iter 203 (2026-02): 单击行高亮 (session-scoped)。selectedProductId 存
  // 当前被点选的 product_id;onSelectProduct(pid) 由父组件切换。整个
  // product 组 (rowspan 分组下的所有 variants) 一起亮。
  selectedProductId = null,
  onSelectProduct,
  // iter 213 (2026-02): 非全屏模式下,Dashboard 分页栏 sticky bottom-0 会占用
  // ~38px。此 prop 让内部 sticky 横向滚动条上抬,避免被分页栏遮挡。默认 0
  // (全屏 / 无分页场景)。
  stickyScrollbarOffset = 0,
  // iter 247: click-to-sort on column headers (session-only, owned by Dashboard).
  sortKey = "product_id",
  sortOrder = "asc",
  onSortChange,
}) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const isAdmin = user && user.role === "admin";
  const [localSelected, setLocalSelected] = useState(new Set());
  const selected = selectedProp ?? localSelected;
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [setTagOpen, setSetTagOpen] = useState(false);
  const [tagValue, setTagValue] = useState("");
  const [savingTag, setSavingTag] = useState(false);
  // iter 159: contract editor for PO 出运日期 (contract-level user field).
  const [contractDialogNo, setContractDialogNo] = useState(null);

  const activeColumns = useMemo(() => {
    // iter 210 (2026-02): checkbox / actions 是系统列(批量选择 + 行内操作),
    // 强制固定在首尾,不受 View 配置控制。老视图或用户在编辑器里意外去掉后
    // 仍能选择产品、触发操作。
    const raw = (visibleColumns && visibleColumns.length ? visibleColumns : DEFAULT_VISIBLE_COLUMNS)
      .filter((id) => COLUMN_BY_ID[id] && id !== "checkbox" && id !== "actions");
    const src = ["checkbox", ...raw, "actions"];
    // iter 199: 紧凑模式下按数据自动收缩列宽; 用户手动 resize (columnWidths[id]
    // 有值) 的列保留手工宽度。舒适模式下继续用现有 columnWidths ?? 默认。
    const autoWidths = density === "compact" ? computeAutoFitWidths(rows, src) : {};
    return src.map((id) => {
      const col = COLUMN_BY_ID[id];
      const manualWidth = columnWidths?.[id];
      let width;
      if (manualWidth != null) {
        width = manualWidth;
      } else if (density === "compact" && COMPACT_FIXED_WIDTHS[id] != null) {
        width = COMPACT_FIXED_WIDTHS[id];
      } else if (autoWidths[id] != null) {
        width = autoWidths[id];
      } else {
        width = col.width;
      }
      return { ...col, width };
    });
  }, [visibleColumns, columnWidths, rows, density]);

  const totalWidth = activeColumns.reduce((sum, c) => sum + c.width, 0);

  // iter 212 (2026-02): 移除顶部水平滚动条,改为**悬浮底部滚动条**
  // (sticky bottom-0)。表格底部原生 overflow-x 滚动条仍存在,但另有
  // 一条 proxy 滚动条钉在浏览器视窗底部,用户无需垂直滚到最底即可
  // 拖动横向。双向同步 scrollLeft;当 ProductTable 整体滚出视窗时
  // sticky 自然消失。
  // ⚠️ 注意:shadcn <Table> 内部有一层 `<div class="w-full overflow-auto">`,
  // 那才是真正的滚动容器。我们的 bottomScrollRef 拿到的是外层 wrapper,
  // 因此在 useEffect 里下钻一层 (`.querySelector('table')?.parentElement`)
  // 找到真正 scrollLeft 生效的元素并挂 scroll listener。
  const bottomScrollRef = useRef(null);
  const stickyScrollRef = useRef(null);
  const getRealScroller = () => {
    const wrap = bottomScrollRef.current;
    if (!wrap) return null;
    // shadcn Table wrapper 是 wrap 里唯一的 <div> child;它才是 overflow-auto。
    // 若结构变动,fallback 到 wrap 自身。
    const tbl = wrap.querySelector("table");
    return tbl?.parentElement || wrap;
  };
  useEffect(() => {
    const bot = getRealScroller();
    const sky = stickyScrollRef.current;
    if (!bot || !sky) return;
    const onBot = () => { if (sky.scrollLeft !== bot.scrollLeft) sky.scrollLeft = bot.scrollLeft; };
    const onSky = () => { if (bot.scrollLeft !== sky.scrollLeft) bot.scrollLeft = sky.scrollLeft; };
    bot.addEventListener("scroll", onBot, { passive: true });
    sky.addEventListener("scroll", onSky, { passive: true });
    return () => {
      bot.removeEventListener("scroll", onBot);
      sky.removeEventListener("scroll", onSky);
    };
  }, [totalWidth]);

  const startResize = (id) => (e) => {
    if (!onColumnResize) return;
    e.preventDefault();
    e.stopPropagation();
    const startX = e.clientX;
    const startWidth = columnWidths?.[id] ?? COLUMN_BY_ID[id].width;
    const minWidth = COLUMN_BY_ID[id].minWidth || 60;
    const onMove = (mv) => {
      const next = Math.max(minWidth, startWidth + (mv.clientX - startX));
      onColumnResize(id, next);
    };
    const onUp = () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
  };

  const startEdit = (row) => {
    setEditingId(row.entry_id);
    setEditForm({
      size: row.size ?? "",
      pcs_per_ctn: row.pcs_per_ctn ?? "",
      purchase_price: row.purchase_price ?? "",
      carton_length: row.carton_length ?? "",
      carton_width: row.carton_width ?? "",
      carton_height: row.carton_height ?? "",
    });
  };

  const cancelEdit = () => { setEditingId(null); setEditForm({}); };
  const updateEditField = (key, value) => setEditForm((f) => ({ ...f, [key]: value }));

  const saveEdit = async (entryId) => {
    const { carton_length, carton_width, carton_height } = editForm;
    const payload = {
      size: editForm.size === "" ? null : editForm.size,
      pcs_per_ctn: editForm.pcs_per_ctn === "" ? null : parseInt(editForm.pcs_per_ctn, 10),
      purchase_price: editForm.purchase_price === "" ? null : parseFloat(editForm.purchase_price),
      carton_length: carton_length === "" ? null : parseFloat(carton_length),
      carton_width: carton_width === "" ? null : parseFloat(carton_width),
      carton_height: carton_height === "" ? null : parseFloat(carton_height),
    };
    setSaving(true);
    try { await onUpdateVariant(entryId, payload); cancelEdit(); }
    finally { setSaving(false); }
  };

  const handleDelete = (row, isLastVariant) => {
    const message = isLastVariant
      ? `This is the only variant for "${row.description || row.item_no}". Deleting it will remove the product from inventory. Confirm delete?`
      : "Delete this variant? This action cannot be undone.";
    if (!window.confirm(message)) return;
    onDeleteVariant(row.entry_id);
  };

  const toggleSelected = (productId) => {
    // iter 123: when lifted state, delegate to parent so the meta record
    // (item_no/description/contract) can be captured for the "Show
    // selected" dialog. Local-state fallback keeps original behavior.
    if (onToggleSelected) {
      const row = rows.find((r) => r.product_id === productId);
      onToggleSelected(productId, row);
      return;
    }
    setLocalSelected((prev) => {
      const next = new Set(prev);
      if (next.has(productId)) next.delete(productId); else next.add(productId);
      return next;
    });
  };

  const allProductIds = useMemo(() => [...new Set(rows.map((r) => r.product_id))], [rows]);
  const allSelected = allProductIds.length > 0 && allProductIds.every((id) => selected.has(id));
  const someSelected = !allSelected && allProductIds.some((id) => selected.has(id));
  const toggleSelectAll = () => {
    if (allSelected) {
      // Un-check all in current page. In lifted mode, toggle each one off.
      if (onToggleSelected) {
        allProductIds.filter((id) => selected.has(id)).forEach((id) => onToggleSelected(id, rows.find((r) => r.product_id === id)));
      } else {
        setLocalSelected(new Set());
      }
    } else {
      if (onToggleSelected) {
        allProductIds.filter((id) => !selected.has(id)).forEach((id) => onToggleSelected(id, rows.find((r) => r.product_id === id)));
      } else {
        setLocalSelected(new Set(allProductIds));
      }
    }
  };

  const handleBulkAddToCart = () => {
    selected.forEach((productId) => onCartClick({ product_id: productId }));
    if (onClearSelected) onClearSelected(); else setLocalSelected(new Set());
  };

  const handleBulkPrintLabel = () => toast.info("Print Label is coming soon, pending format template confirmation");

  const openSetTagDialog = () => { setTagValue(""); setSetTagOpen(true); };

  const handleConfirmSetTag = async () => {
    setSavingTag(true);
    try {
      await onBulkSetTag(Array.from(selected), tagValue);
      toast.success(`Tag updated for ${selected.size} product(s)`);
      setSetTagOpen(false);
      if (onClearSelected) onClearSelected(); else setLocalSelected(new Set());
    } catch {
      toast.error("Failed to update tag");
    } finally {
      setSavingTag(false);
    }
  };

  if (loading) {
    return <div className="bg-white border border-gray-200 rounded-xl py-24 text-center text-sm text-gray-400" data-testid="products-loading">Loading...</div>;
  }

  if (rows.length === 0) {
    return (
      <div className="bg-white border border-gray-200 rounded-xl py-24 text-center" data-testid="products-empty-state">
        <p className="text-sm text-gray-400">No products found matching your criteria</p>
      </div>
    );
  }

  const groupSize = computeGroups(rows);

  const renderHeader = (col) => {
    if (col.id === "checkbox") {
      return (
        <Checkbox
          data-testid="product-select-all-checkbox"
          checked={allSelected ? true : someSelected ? "indeterminate" : false}
          onCheckedChange={toggleSelectAll}
          aria-label="Select all products"
        />
      );
    }
    // iter 178v: translate column header via i18n; fall back to the module
    // constant's English `label` so any new column that hasn't been mapped
    // yet still renders correctly.
    const key = `dashboard.columns_labels.${col.id}`;
    const translated = t(key);
    const label = translated === key ? col.label : translated;
    if (col.id === "sales_price") return `${label} (${priceCurrency})`;
    return label;
  };

  return (
    <div className="space-y-3">
      {/* iter 178h: 内部黑色批量条已删除,与 Dashboard 顶部的 Action 菜单合并 */}
      {/* iter 198 (2026-02): density="compact" — Excel 风格紧凑网格 (12px 字号,
          完整 border-r/border-b 网格线,单元格 padding 极小,无外壳圆角)。
          用 CSS 后代选择器一次性覆盖,不改动子组件。 */}
      {/* iter 213 → iter 226 (2026-02): 之前给根加 flex-1 min-h-0 是想让白色
          表格卡片填满 Dashboard 剩余高度,但那让 sticky 底部横向滚动条的"包含
          块"缩到根内部,导致 sticky 无法抵达视窗底部 = 滚动条失效。回退成
          space-y-3(无 flex),sticky 容器块回到 body,滚动条正常吸底。 */}
      <div
        className={
          density === "compact"
            ? "bg-white border border-gray-300 overflow-hidden [&_table]:text-[12px] [&_th]:!h-8 [&_th]:!px-1.5 [&_th]:!py-1 [&_th]:!bg-gray-100 [&_th]:!text-gray-700 [&_th]:!border-r [&_th]:!border-gray-300 [&_th]:!leading-tight [&_th:last-child]:!border-r-0 [&_td]:!p-1 [&_td]:!border-r [&_td]:!border-gray-200 [&_td]:!leading-tight [&_td]:!align-top [&_td:last-child]:!border-r-0 [&_tr]:!border-b [&_tr]:!border-gray-300 [&_tr:not(.row-selected):hover]:!bg-blue-50/40"
            : "bg-white border border-gray-200 rounded-xl overflow-hidden"
        }
        data-testid="products-table-container"
        data-density={density}
      >
        {/* iter 212 (2026-02): 顶部水平滚动条已移除,改用底部悬浮 sticky
            滚动条(见外层 space-y-3 容器末尾)。实际横向 scroll 发生在
            shadcn <Table> 内部的 wrapper 上(bot.querySelector('table').parentElement),
            所以这里不再挂 JSX onScroll — 见 useEffect 里 addEventListener。 */}
        <div ref={bottomScrollRef}>
          <Table className={density === "compact" ? "text-[12px]" : "text-[14px]"} style={{ tableLayout: "fixed", width: totalWidth }}>
            <colgroup>
              {activeColumns.map((c) => <col key={c.id} style={{ width: c.width }} />)}
            </colgroup>
            <TableHeader>
              <TableRow className="hover:bg-transparent bg-gray-50 border-b border-gray-200">
                {activeColumns.map((c) => {
                  const sk = COLUMN_SORT_KEYS[c.id];
                  const sortable = !!sk && !!onSortChange;
                  const active = sortable && sortKey === sk;
                  return (
                  <ResizableTh
                    key={c.id}
                    id={c.id}
                    width={c.width}
                    align={c.align === "right" ? "right" : undefined}
                    onResizeStart={startResize(c.id)}
                    sortable={sortable}
                    sortDir={active ? sortOrder : null}
                    onSort={() => {
                      if (!active) onSortChange(sk, "asc");
                      else if (sortOrder === "asc") onSortChange(sk, "desc");
                      else onSortChange("product_id", "asc");
                    }}
                    data-testid={`column-header-${c.id}`}
                  >
                    {renderHeader(c)}
                  </ResizableTh>
                  );
                })}
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((r, idx) => {
                const isGroupStart = groupSize[idx] > 0;
                const isEditing = editingId === r.entry_id;
                const hqContainerQty = isEditing
                  ? computeContainerQty(editForm.carton_length, editForm.carton_width, editForm.carton_height, editForm.pcs_per_ctn)
                  : computeContainerQty(r.carton_length, r.carton_width, r.carton_height, r.pcs_per_ctn);
                const isLastVariant = rows.filter((x) => x.product_id === r.product_id).length === 1;
                return (
                  <TableRow
                    key={r.entry_id}
                    data-testid={`product-row-${r.entry_id}`}
                    className={`border-b border-gray-100 cursor-pointer ${
                      selectedProductId === r.product_id
                        ? "row-selected !bg-amber-50 hover:!bg-amber-100"
                        : "hover:bg-gray-50/60"
                    }`}
                    onClick={(e) => {
                      if (isEditing) return;
                      // iter 203: single-click selects + fires the legacy
                      // row-click callback (opens detail).
                      if (onSelectProduct) onSelectProduct(r.product_id);
                      onRowClick(r);
                    }}
                  >
                    {activeColumns.map((c) => {
                      if (c.level === "product") {
                        return (
                          <ProductCell
                            key={c.id}
                            colId={c.id}
                            row={r}
                            isGroupStart={isGroupStart}
                            groupRowSpan={groupSize[idx]}
                            selected={selected}
                            toggleSelected={toggleSelected}
                            onAddVariant={onAddVariant}
                            onToggleBulky={onToggleBulky}
                            onLookupSimilar={onLookupSimilar}
                            onRevertManual={onRevertManual}
                            density={density}
                          />
                        );
                      }
                      if (c.id === "actions") {
                        return isEditing ? (
                          <TableCell key="actions" className="py-2" onClick={(e) => e.stopPropagation()}>
                            <div className="flex items-center justify-end gap-1.5">
                              <button data-testid={`save-variant-${r.entry_id}`} disabled={saving} onClick={() => saveEdit(r.entry_id)} className="w-7 h-7 rounded-md flex items-center justify-center text-emerald-600 hover:bg-emerald-50 transition-colors disabled:opacity-40">
                                <Check size={14} strokeWidth={2} />
                              </button>
                              <button data-testid={`cancel-edit-${r.entry_id}`} onClick={cancelEdit} className="w-7 h-7 rounded-md flex items-center justify-center text-gray-400 hover:bg-gray-100 transition-colors">
                                <X size={14} strokeWidth={2} />
                              </button>
                            </div>
                          </TableCell>
                        ) : (
                          <TableCell key="actions" className="py-2" onClick={(e) => e.stopPropagation()}>
                            <div className="flex items-center justify-end gap-1.5">
                              <button data-testid={`edit-variant-${r.entry_id}`} onClick={() => startEdit(r)} title="Edit Variant" className="w-7 h-7 rounded-md flex items-center justify-center text-gray-400 hover:text-brand-600 hover:bg-brand-50 transition-colors">
                                <Pencil size={13} strokeWidth={1.75} />
                              </button>
                              {isAdmin && (
                                <button data-testid={`delete-variant-${r.entry_id}`} onClick={() => handleDelete(r, isLastVariant)} title="Delete Variant" className="w-7 h-7 rounded-md flex items-center justify-center text-gray-400 hover:text-red-600 hover:bg-red-50 transition-colors">
                                  <Trash2 size={13} strokeWidth={1.75} />
                                </button>
                              )}
                            </div>
                          </TableCell>
                        );
                      }
                      return (
                        <VariantCell
                          key={c.id}
                          colId={c.id}
                          row={r}
                          priceCurrency={priceCurrency}
                          isEditing={isEditing}
                          editForm={editForm}
                          hqContainerQty={hqContainerQty}
                          updateEditField={updateEditField}
                          isAdmin={isAdmin}
                          onCartClick={onCartClick}
                          onRowClick={onRowClick}
                          onToggleApproval={onToggleApproval}
                          onOpenContract={setContractDialogNo}
                        />
                      );
                    })}
                  </TableRow>
                );
              })}
              {(() => {
                // iter 110 / iter 180 (2026-02): Total row for numeric
                // columns. Rendered when any of the summable columns is
                // visible: qty (数量), shipping_qty (出货数量), amount
                // (Sales × Qty). Values come from server-side `totals`
                // (aggregated across ALL filtered variants, not just the
                // current page) — see /api/inventory response. Falls back
                // to page-only sums when the prop is missing (standalone
                // usage / tests).
                const SUMMABLE = ["qty", "shipping_qty", "amount"];
                const visibleSummable = activeColumns.filter((c) => SUMMABLE.includes(c.id));
                if (visibleSummable.length === 0 || rows.length === 0) return null;
                const fallback = {
                  qty: rows.reduce((s, r) => s + (r.qty != null ? Number(r.qty) : 0), 0),
                  shipping_qty: rows.reduce((s, r) => s + (r.shipping_qty != null ? Number(r.shipping_qty) : 0), 0),
                  amount: rows.reduce((s, r) => s + (r.amount != null ? Number(r.amount) : 0), 0),
                };
                const values = {
                  qty: totals?.qty ?? fallback.qty,
                  shipping_qty: totals?.shipping_qty ?? fallback.shipping_qty,
                  amount: totals?.amount ?? fallback.amount,
                };
                const fmt = {
                  qty: (v) => Number(v).toLocaleString(),
                  shipping_qty: (v) => Number(v).toLocaleString(),
                  amount: (v) => Number(v).toFixed(2),
                };
                // Show the "Total" label on exactly one non-summable column
                // (picked in this priority: description > first non-summable
                // column). This avoids the double-label bug where both the
                // first column AND description would independently qualify.
                let labelColId = activeColumns.find((c) => c.id === "description" && !SUMMABLE.includes(c.id))?.id;
                if (!labelColId) {
                  labelColId = activeColumns.find((c) => !SUMMABLE.includes(c.id))?.id;
                }
                return (
                  <TableRow className="bg-gray-50 border-t-2 border-gray-300 font-semibold" data-testid="product-totals-row">
                    {activeColumns.map((c) => {
                      if (SUMMABLE.includes(c.id)) {
                        return (
                          <TableCell
                            key={c.id}
                            className="font-mono text-sm text-right text-gray-900"
                            data-testid={`product-${c.id}-total-value`}
                          >
                            {fmt[c.id](values[c.id])}
                          </TableCell>
                        );
                      }
                      return (
                        <TableCell key={c.id} className="text-xs text-gray-500">
                          {c.id === labelColId ? (
                            <span className="uppercase tracking-wider text-[10px] font-mono">{t("common.total")}</span>
                          ) : ""}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                );
              })()}
            </TableBody>
          </Table>
        </div>
      </div>

      {/* iter 212 (2026-02): 悬浮底部水平滚动条 — 跟随页面垂直滚动始终贴在
          浏览器视窗底部,直到整个 ProductTable 滚出视窗。overflow-x 由 spacer
          撑开宽度;和上面 bottomScrollRef 通过 useEffect 双向同步。 */}
      <div
        ref={stickyScrollRef}
        data-testid="products-sticky-scrollbar"
        onScroll={(e) => {
          const b = bottomScrollRef.current;
          if (b && b.scrollLeft !== e.currentTarget.scrollLeft) b.scrollLeft = e.currentTarget.scrollLeft;
        }}
        className="sticky z-20 overflow-x-auto overflow-y-hidden bg-white/95 backdrop-blur border-t border-gray-300 shadow-[0_-2px_6px_rgba(0,0,0,0.04)]"
        style={{ height: 14, bottom: stickyScrollbarOffset }}
      >
        <div style={{ width: totalWidth, height: 1 }} />
      </div>

      <Dialog open={setTagOpen} onOpenChange={setSetTagOpen}>
        <DialogContent className="rounded-xl sm:max-w-sm" data-testid="set-tag-dialog">
          <DialogHeader>
            <DialogTitle>Set Tag for {selected.size} Product(s)</DialogTitle>
          </DialogHeader>
          <div>
            <Label className="text-xs font-medium text-gray-500">New Tag Value</Label>
            <Input data-testid="set-tag-input" value={tagValue} onChange={(e) => setTagValue(e.target.value)} placeholder="Enter tag value" className="mt-1.5 rounded-lg border-gray-300 focus-visible:ring-brand-500" autoFocus />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSetTagOpen(false)} className="rounded-lg">Cancel</Button>
            <Button data-testid="confirm-set-tag-button" onClick={handleConfirmSetTag} disabled={savingTag} className="rounded-lg bg-[#1677ff] hover:bg-[#0958d9]">
              {savingTag ? "Saving..." : "Apply"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <ContractDialog
        open={!!contractDialogNo}
        onOpenChange={(v) => { if (!v) setContractDialogNo(null); }}
        contractNo={contractDialogNo}
        onSaved={() => {
          // A save fans-out po_shipping_date to all matching price_entries;
          // the currently rendered rows don't auto-refresh, so hint the
          // parent to reload. Consumers listen via onRowClick-style events
          // aren't wired here, so we simply toast (the shown data becomes
          // stale until next fetch, which is acceptable).
        }}
      />
    </div>
  );
}
